import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssessmentStatusWidgetModelInterface } from "../models";
import './AssessmentStatus.scss';
import { DonutChart, StatusType, Student } from "@/components/Global/DonutChart/DonutChart";
import { AssessmentCalendarDays } from "./AssessmentCalendar/AssessmentCalendar";
import tablerReloadIcon from "@/images/icons/tabler_reload.svg";
import chartOutlineIcon from "@/images/icons/chart-outline.svg";
import { StudentListModal } from "./StudentListModal/StudentListModal";
import calendarIcon from "@/images/icons/calendar-month-purple.svg"
import { AsyncLoadingState } from "@/features/dashboard/enums";
import { useSelector } from "react-redux";
import { RootState } from "@/models/RootState.type";
import { AssessmentWindow } from "../utils/findAssessmentWindow";
import { AssessmentPeriodItem, AssessmentPeriodWindowType, useAssessmentPeriodWindows } from "@/hooks/useAssessmentPeriods";
import WidgetDataReloadButton from "./WidgetDataReloadButton";

export type AssessmentStatusWidgetProps = {
  data: AssessmentStatusWidgetModelInterface
  widgetLoadingState: string
  status: string
  onRetry: () => void
  assessmentWindowStatus?: AssessmentWindow
}

export const WidgetAssessmentStatus: React.FC<AssessmentStatusWidgetProps> = ({ data, status, onRetry, assessmentWindowStatus }) => {
  const { t } = useTranslation('dashboard');
  const assessmentWindows = useAssessmentPeriodWindows();


  const loading = useSelector((state: RootState) => state.assessmentStatusWidget.loading);

  if (status === 'error' || loading === AsyncLoadingState.ERROR) {
    return <AssessmentStatusError onRetry={onRetry} />
  }

  const {
    completePercentage,
    totalAssessmentCount,
    completeCount,
    scheduledCount,
    inProgressCount,
    underReviewCount,
    requireActionCount,
    students,
    completedDate,
  } = data;

  const hasActive = assessmentWindowStatus?.activeDistrictWindow;
  const hasUpcoming = assessmentWindowStatus?.upcomingWindow;
  const hasPrevious = assessmentWindowStatus?.previousWindow;
  const notInAssessmentWindowAndHasUpcoming = !assessmentWindowStatus?.activeDistrictWindow && assessmentWindowStatus?.upcomingWindow;
  const noWindows = !assessmentWindowStatus?.activeDistrictWindow && !assessmentWindowStatus?.upcomingWindow && !assessmentWindowStatus?.previousWindow;


  const districtScreeningSetup = !!assessmentWindows.assessmentPeriodWindows.find(window => window.screeningWindowType === AssessmentPeriodWindowType.DISTRICT_ASSIGNMENT);

  const chartData = [
    { type: StatusType.COMPLETE, value: completeCount },
    { type: StatusType.SCHEDULED, value: scheduledCount },
    { type: StatusType.IN_PROGRESS, value: inProgressCount },
    { type: StatusType.UNDER_REVIEW, value: underReviewCount },
    { type: StatusType.ACTION_NEEDED, value: requireActionCount }
  ];

  const [isModalOpen, setIsModalOpen] = useState(false);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleChartButtonClick = () => {
    setIsModalOpen(true);
  };

  const renderModal = () => (
    <StudentListModal
      isOpen={isModalOpen}
      onCloseHandler={closeModal}
      students={students as Student[]}
      remainingDays={assessmentWindowStatus?.daysLeftInCurrentAssessmentWindow!}
      daysUntilNextAssessmentWindowOpens={assessmentWindowStatus?.daysUntilNextAssessmentWindowOpens as number}
      assessmentWindow={assessmentWindowStatus?.activeAssessmentWindow!}
      completedDate={completedDate as string}
      hasActive={hasActive as boolean}
      hasUpcoming={hasUpcoming as boolean}
      assessmentWindowStatus={assessmentWindowStatus as AssessmentWindow}
      noAssessmentWindow={noWindows}
      districtScreeningSetup={districtScreeningSetup}
    />
  );

  //anytime donut chart is rendered totalAssessmentCount must be greater than 0
  if (hasActive && totalAssessmentCount > 0) {
    return (
      <div className="widget-body widget-assess-body">
        {renderModal()}
        <button className="chart-button" onClick={handleChartButtonClick}>
          <img
            src={chartOutlineIcon}
            className="chart-icon"
          />
        </button>
        <DonutChart
          data={chartData}
          completePercentage={completePercentage}
          studentList={students as Student[]}
          remainingDays={assessmentWindowStatus?.daysLeftInCurrentAssessmentWindow!}
          assessmentWindow={assessmentWindowStatus?.activeAssessmentWindow!}
          completedDate={completedDate as string}
          hasUpcoming={hasUpcoming as boolean}
          hasActive={hasActive as boolean}
          daysUntilNextAssessmentWindowOpens={assessmentWindowStatus?.daysUntilNextAssessmentWindowOpens as number}
          assessmentWindowStatus={assessmentWindowStatus as AssessmentWindow}
          noAssessmentWindow={noWindows}
          districtScreeningSetup={districtScreeningSetup}
        />
        <AssessmentStatusDays
          assessmentWindowDaysRemaining={assessmentWindowStatus?.daysLeftInCurrentAssessmentWindow!}
          hasActive={hasActive}
          hasUpcoming={hasUpcoming}
          noAssessmentWindow={noWindows}
          assessmentWindowDaysToNext={assessmentWindowStatus?.daysUntilNextAssessmentWindowOpens!}
          handleCalendarIconClick={handleChartButtonClick}
        />
      </div>
    );
  } else if (!hasActive && totalAssessmentCount > 0) {
    return (
      <div className="widget-body widget-assess-body">
        {renderModal()}
        <button className="chart-button" onClick={handleChartButtonClick}>
          <img
            src={chartOutlineIcon}
            className="chart-icon"
          />
        </button>
        <DonutChart
          data={chartData}
          completePercentage={completePercentage}
          studentList={students as Student[]}
          remainingDays={assessmentWindowStatus?.daysLeftInCurrentAssessmentWindow!}
          assessmentWindow={assessmentWindowStatus?.activeAssessmentWindow!}
          completedDate={completedDate as string}
          hasUpcoming={hasUpcoming as boolean}
          hasActive={hasActive as boolean}
          daysUntilNextAssessmentWindowOpens={assessmentWindowStatus?.daysUntilNextAssessmentWindowOpens as number}
          assessmentWindowStatus={assessmentWindowStatus as AssessmentWindow}
          noAssessmentWindow={noWindows}
          districtScreeningSetup={districtScreeningSetup}
        />
        <AssessmentStatusDays
          assessmentWindowDaysRemaining={assessmentWindowStatus?.daysLeftInCurrentAssessmentWindow!}
          hasActive={hasActive}
          hasUpcoming={hasUpcoming}
          noAssessmentWindow={noWindows}
          assessmentWindowDaysToNext={assessmentWindowStatus?.daysUntilNextAssessmentWindowOpens!}
          handleCalendarIconClick={handleChartButtonClick}
        />
      </div>
    );
  } else if (totalAssessmentCount === 0 && notInAssessmentWindowAndHasUpcoming && !hasPrevious) {
    return (
      <div className="widget-body widget-assess-body">
        {renderModal()}
        <div className="schedule-body">
          <button className="schedule-icon-button" onClick={handleChartButtonClick}>
            <img src={calendarIcon} alt="calendar" className="calendar-icon" />
            <div className="schedule-body-text">
              {t('SCHEDULE')}
            </div>
          </button>
        </div>
        <AssessmentStatusDays
          assessmentWindowDaysToNext={assessmentWindowStatus?.daysUntilNextAssessmentWindowOpens!}
          hasUpcoming={hasUpcoming}
          handleCalendarIconClick={handleChartButtonClick}
        />
      </div>
    );
  } else if (noWindows && totalAssessmentCount === 0) {
    return (
      <div className="widget-body widget-assess-body">
        {renderModal()}
        <div className="schedule-body">
          <button className="schedule-icon-button" onClick={handleChartButtonClick}>
            <img src={calendarIcon} alt="calendar" className="calendar-icon" />
            <div className="schedule-body-text">
              {t('SCHEDULE')}
            </div>
          </button>
        </div>
        <AssessmentStatusDays
          assessmentWindowDaysToNext={assessmentWindowStatus?.daysUntilNextAssessmentWindowOpens!}
          noAssessmentWindow={noWindows}
          handleCalendarIconClick={handleChartButtonClick}
        />
      </div>
    );
  } else if (hasUpcoming && totalAssessmentCount === 0) {
    return (
      <div className="widget-body widget-assess-body">
        {renderModal()}
        <div className="schedule-body">
          <button className="schedule-icon-button" onClick={handleChartButtonClick}>
            <img src={calendarIcon} alt="calendar" className="calendar-icon" />
            <div className="schedule-body-text">
              {t('SCHEDULE')}
            </div>
          </button>
        </div>
        <AssessmentStatusDays
          assessmentWindowDaysToNext={assessmentWindowStatus?.daysUntilNextAssessmentWindowOpens!}
          hasUpcoming={hasUpcoming as boolean}
          handleCalendarIconClick={handleChartButtonClick}
        />
      </div>
    );
  } else if (!hasUpcoming && hasActive && totalAssessmentCount === 0) {
    return (
      <div className="widget-body widget-assess-body">
        {renderModal()}
        <div className="schedule-body">
          <button className="schedule-icon-button" onClick={handleChartButtonClick}>
            <img src={calendarIcon} alt="calendar" className="calendar-icon" />
            <div className="schedule-body-text">
              {t('SCHEDULE')}
            </div>
          </button>
        </div>
        <AssessmentStatusDays
          assessmentWindowDaysRemaining={assessmentWindowStatus?.daysLeftInCurrentAssessmentWindow!}
          hasActive={hasActive}
          hasUpcoming={hasUpcoming}
          handleCalendarIconClick={handleChartButtonClick}
        />
      </div>
    );
  } else if (!districtScreeningSetup && noWindows && totalAssessmentCount === 0) {
    return (
      <div className="widget-body widget-assess-body">
        {renderModal()}
        <div className="schedule-body">
          <button className="schedule-icon-button" onClick={handleChartButtonClick}>
            <img src={calendarIcon} alt="calendar" className="calendar-icon" />
            <div className="schedule-body-text">
              {t('SCHEDULE')}
            </div>
          </button>
        </div>
        <AssessmentStatusDays
          assessmentWindowDaysRemaining={assessmentWindowStatus?.daysLeftInCurrentAssessmentWindow!}
          noAssessmentWindow={noWindows}
          hasActive={hasActive}
          hasUpcoming={hasUpcoming}
          handleCalendarIconClick={handleChartButtonClick}
        />
      </div>
    );
  }
  return null;
};

const AssessmentStatusDays = ({
  assessmentWindowDaysRemaining,
  assessmentWindowDaysToNext,
  hasActive,
  hasUpcoming,
  noAssessmentWindow,
  handleCalendarIconClick
}: {
  assessmentWindowDaysRemaining?: number;
  assessmentWindowDaysToNext?: number;
  hasActive?: boolean;
  hasUpcoming?: boolean;
  noAssessmentWindow?: boolean;
  handleCalendarIconClick?: () => void;
}) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { t: tAssessmentModal } = useTranslation('assessment_modal');

  const displayState = getDisplayState();



  function getDisplayState() {
    if (noAssessmentWindow) return 'no-window'; // no district window scheduled at all
    if (!hasActive && !hasUpcoming) return 'no-window'; // no active or upcoming window
    if (hasActive && hasUpcoming) return 'active'; // active window with an upcoming window
    if (hasActive && !hasUpcoming) return 'active'; // active window with no upcoming window
    if (hasUpcoming) return 'next'; // upcoming window
    return 'default';
  }

  if (displayState === 'no-window') {
    return (
      <div className="days-left-content">
        <div className="days-left-content-inner" style={{ width: "75%" }}>
          <div className="days-left-content-icon">
          </div>
          <div className="days-left-content-label d-flex flex-column align-items-start">
            <span className="days-left-content-label-text">
              {t('NO_ASSESS_WINDOW')}
            </span>
          </div>
          <button className="calendar-icon-button" onClick={handleCalendarIconClick}>
            <img src={calendarIcon} alt="calendar" className="calendar-icon" />
          </button>
        </div>
      </div>
    );
  }

  const displayProps = {
    'active': {
      daysValue: assessmentWindowDaysRemaining || 0,
      messageLine1: assessmentWindowDaysRemaining ? (assessmentWindowDaysRemaining > 1 ? tAssessmentModal('assessment_window_days') : tAssessmentModal('assessment_window_day')) : '',
      messageLine2: assessmentWindowDaysRemaining ? tAssessmentModal('assessment_window_days_remaining') : '',
      className: ''
    },
    'next': {
      daysValue: assessmentWindowDaysToNext || 0,
      messageLine1: assessmentWindowDaysToNext ? (assessmentWindowDaysToNext > 1 ? tAssessmentModal('assessment_window_days_to_next_plural') : tAssessmentModal('assessment_window_days_to_next')) : '',
      messageLine2: assessmentWindowDaysToNext ? tAssessmentModal('assessment_window_opens') : '',
      className: 'next-assessment-window'
    },
    'default': {
      daysValue: 0,
      messageLine1: '',
      messageLine2: '',
      className: ''
    }
  }[displayState];

  return (
    <div className="days-left-content">
      <div className="days-left-content-inner">
        <div className="days-left-content-icon">
          <AssessmentCalendarDays days={displayProps.daysValue} />
        </div>
        <div className="days-left-content-label d-flex flex-column align-items-start">
          <span className={`days-left-content-label-text ${displayProps.className}`}>
            {displayProps.messageLine1}
          </span>
          <span className={`days-left-content-label-text ${displayProps.className}`}>
            {displayProps.messageLine2}
          </span>
        </div>
        <button className="calendar-icon-button" onClick={handleCalendarIconClick}>
          <img src={calendarIcon} alt="calendar" className="calendar-icon" />
        </button>
      </div>
    </div>
  );
}

export const AssessmentStatusError = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation('dashboard');
  return (
    <div className="widget-body widget-assess-body assess-status-error d-flex flex-column align-items-center justify-content-center text-center">
      <div className="d-flex flex-column align-items-center justify-content-center assess-status-error-inner">
        <div className="data-failed-text mb-3 font-weight-bold">{t('DATA_FAILED_TO_LOAD')}</div>
        <WidgetDataReloadButton onReload={onRetry} />
      </div>
    </div>
  );
}